import { apiV2WaterMapConfigDetailPost, V2WaterMapConfigDetailPostResponse } from '@maxtropy/device-customer-apis-v2';
import { useRequest } from 'ahooks';
import { Space } from 'antd';
import classNames from 'classnames';
import dayjs, { Dayjs } from 'dayjs';
import qs from 'qs';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Modal } from '@maxtropy/components';
import { ShareDataWrapperContext } from '../..';
import CenterTopBox from './components/CenterTopBox';
import LeftBox from './components/LeftBox';
import RightBox from './components/RightBox';
import styles from './index.module.scss';
import UnityMap from './components/UnityMap';
import { UnityClickDetail } from '../UnityRenderMap';
import AiAssistant from '../AiAssistant';
import UseWaterEnergyFlow, { formatTime } from './components/LeftBox/components/UseWaterEnergyFlow';
import { DateTypeByWater } from './components/LeftBox/components/UseWaterEnergyFlow/FlowSwitchDate';
import { DoubleRightOutlined } from '@ant-design/icons';
import ScadaGraphPage from '@/pages/ScadaGraphPage';
import type { WgqMapMode } from '../..';
import FlowmeterModal, { FlowmeterData } from './components/FlowMeterModal';
import FloatingMoreActions from '../FloatingMoreActions';
import { useSharedUnityMap } from '../SharedUnityMapLayer';

interface ShareWaterConfigDataProps {
  configDetail?: V2WaterMapConfigDetailPostResponse;
}

export const ShareConfigDataWaterContext = createContext<ShareWaterConfigDataProps>({});

type FlowRouteParams = {
  flowTimeResolution: DateTypeByWater;
  rangeTime?: [Dayjs | null, Dayjs | null] | null;
};

const AI_ASSISTANT_SIZE = { width: 96, height: 128 };
const WATER_PIPELINE_PATHS = ['ps', 'he'];
const XRAY_MODE_TIMEOUT = 2000;

const getUnityBooleanResult = (value: unknown, fallback: boolean) => {
  if (typeof value === 'boolean') return value;
  if (value === 'true') return true;
  if (value === 'false') return false;
  if (value && typeof value === 'object') {
    const data = value as Record<string, unknown>;
    if (typeof data.enabled === 'boolean') return data.enabled;
    if (typeof data.data === 'boolean') return data.data;
    if (typeof data.value === 'boolean') return data.value;
  }
  return fallback;
};

const withUnityTimeout = async <T,>(promise: Promise<T>, timeout: number, message: string): Promise<T> => {
  let timer: number | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        timer = window.setTimeout(() => {
          reject(new Error(message));
        }, timeout);
      }),
    ]);
  } finally {
    if (timer) {
      window.clearTimeout(timer);
    }
  }
};

const getFlowmeterFromUnityClick = (detail: UnityClickDetail): FlowmeterData | undefined => {
  const data = detail.data as Record<string, any> | undefined;
  const properties = data?.properties?.properties ?? data?.properties ?? data;
  const flowmeterId = Number(properties?.flowmeterId);
  if (!Number.isFinite(flowmeterId)) return undefined;

  return {
    flowmeterId,
    flowmeterName: properties.flowmeterName,
    dayTotal: properties.dayTotal,
    monthTotal: properties.monthTotal,
    longitude: properties.longitude,
    latitude: properties.latitude,
  };
};

const UnityWaterMap: React.FC<{ mapMode?: WgqMapMode }> = ({ mapMode = 'unityMap' }) => {
  const { isFullScreen, isStandardSize, loadMap, scale: pageScale } = useContext(ShareDataWrapperContext);
  const { unityReady, callUnity } = useSharedUnityMap();
  const [flowModalOpen, setFlowModalOpen] = useState<boolean>(false);
  const [selectedFlowmeter, setSelectedFlowmeter] = useState<FlowmeterData>();
  const [isXrayMode, setIsXrayMode] = useState(false);
  const isXrayModeRef = useRef(false);

  // AI助手定位
  const aiAnchorRef = useRef<HTMLDivElement>(null);
  const [flowRouteParams, setFlowRouteParams] = useState<FlowRouteParams>({
    flowTimeResolution: DateTypeByWater.DAY,
    rangeTime: [dayjs().startOf('month').startOf('D'), dayjs().endOf('D')],
  });

  // 查询配置
  const { data: configDetail } = useRequest(async () => {
    return await apiV2WaterMapConfigDetailPost({});
  });

  useEffect(() => {
    isXrayModeRef.current = isXrayMode;
  }, [isXrayMode]);

  const setPipelineVisible = useCallback(
    async (active: boolean) => {
      if (!callUnity) {
        return;
      }

      await Promise.all(
        WATER_PIPELINE_PATHS.map(async path => {
          const payload = {
            type: 'setObjectActive',
            data: {
              path,
              active,
            },
          };

          try {
            return await withUnityTimeout(
              callUnity(payload),
              XRAY_MODE_TIMEOUT,
              `[UnityWaterMap] setObjectActive timeout: ${path}`
            );
          } catch (error) {
            console.error('[UnityWaterMap] setObjectActive failed', { path, active, error });
            return undefined;
          }
        })
      );
    },
    [callUnity]
  );

  const closeXrayMode = useCallback(async () => {
    if (!callUnity) {
      return;
    }

    await setPipelineVisible(false);

    const payload = {
      type: 'setXrayMode',
      data: {
        enabled: false,
      },
    };
    await withUnityTimeout(callUnity(payload), XRAY_MODE_TIMEOUT, '[UnityWaterMap] close setXrayMode timeout').catch(
      error => {
        console.error('[UnityWaterMap] close xray mode failed', error);
      }
    );
  }, [callUnity, setPipelineVisible]);

  useEffect(() => {
    if (loadMap && mapMode === 'unityMap') return;
    if (isXrayModeRef.current) {
      void closeXrayMode();
    }
    setIsXrayMode(false);
  }, [closeXrayMode, loadMap, mapMode]);

  useEffect(() => {
    return () => {
      if (!isXrayModeRef.current) return;
      void closeXrayMode();
    };
  }, [closeXrayMode]);

  const handleClickXray = useCallback(async () => {
    if (!unityReady || !callUnity) {
      return;
    }

    const enable = !isXrayMode;

    try {
      const payload = {
        type: 'setXrayMode',
        data: {
          enabled: enable,
        },
      };
      const result = await withUnityTimeout(
        callUnity(payload),
        XRAY_MODE_TIMEOUT,
        '[UnityWaterMap] setXrayMode timeout'
      ).catch(() => undefined);
      const nextXrayMode = getUnityBooleanResult(result, enable);

      await setPipelineVisible(nextXrayMode);
      setIsXrayMode(nextXrayMode);
    } catch (error) {
      console.error('[UnityWaterMap] switch xray mode failed', error);
      if (enable) {
        void closeXrayMode();
      }
    }
  }, [callUnity, closeXrayMode, isXrayMode, setPipelineVisible, unityReady]);

  const renderCenterStandardScreen = useMemo(() => {
    return (
      <div className={styles.standard_no_map_scroll}>
        <div className={classNames(styles.content_layer, styles.content_layer_no_map)}>
          <div className={classNames(styles.center_left_box, styles.noMapColumn)} style={{ flex: 1 }}>
            <LeftBox />
          </div>
          <div className={classNames(styles.center_right_box, styles.noMapColumn)} style={{ flex: 1 }}>
            <RightBox />
          </div>
        </div>
      </div>
    );
  }, []);

  const renderEnergyFlowButton = () => {
    if (!loadMap) return null;

    return (
      <button className={styles.energyFlowButton} type="button" onClick={() => setFlowModalOpen(true)}>
        用水能流概览
      </button>
    );
  };

  const renderXrayButton = () => {
    if (!loadMap || mapMode !== 'unityMap') return null;

    return (
      <div className={styles.xrayLegendList}>
        <button
          className={classNames(styles.xrayButton, isXrayMode ? styles.xrayButtonActive : '')}
          type="button"
          onClick={handleClickXray}
          disabled={!unityReady || !callUnity}
          title="切换透视模式"
        >
          透视模式
        </button>
      </div>
    );
  };

  const handleFlowRouteParamsChange = useCallback((params: FlowRouteParams) => {
    setFlowRouteParams(params);
  }, []);

  const handleOpenEnergyFlowDetail = () => {
    if (!flowRouteParams.rangeTime?.[0] || !flowRouteParams.rangeTime?.[1]) return;
    const time = formatTime(flowRouteParams.rangeTime as [Dayjs, Dayjs], flowRouteParams.flowTimeResolution);

    window.open(
      `/energy/analysis/unitSankey?${qs.stringify(
        {
          radioType: flowRouteParams.flowTimeResolution,
          fromTime: time.fromTime,
          toTime: time.toTime,
          unitGroupId: configDetail?.energyGroupId1,
        },
        { indices: false }
      )} `,
      '_blank'
    );
  };

  const handleUnityClick = useCallback((detail: UnityClickDetail) => {
    const flowmeter = getFlowmeterFromUnityClick(detail);
    if (flowmeter) {
      setSelectedFlowmeter(flowmeter);
      return;
    }

    if (detail?.data?.name === '自来水加压泵站') {
      window.open('/scada/28', '_blank');
    }
  }, []);

  const renderMapView = (className: string, children?: React.ReactNode, style?: React.CSSProperties) => {
    if (mapMode === 'unityMap') {
      return (
        <UnityMap className={className} onUnityClick={handleUnityClick}>
          {children}
        </UnityMap>
      );
    }

    return (
      <div className={className} style={style}>
        {configDetail?.configurationId ? (
          <ScadaGraphPage
            key={configDetail.configurationId}
            styleHeight="100%"
            scadaId={configDetail.configurationId}
            backgroundColor="transparent"
          />
        ) : null}
        {children}
      </div>
    );
  };

  return (
    <ShareConfigDataWaterContext.Provider value={{ configDetail }}>
      {isStandardSize ? (
        loadMap ? (
          <div
            className={`${styles.inner_center} ${styles.inner_center_standard}`}
            style={{ background: !loadMap ? '#111D2C' : 'transparent' }}
          >
            <div style={{ position: 'relative', height: '100%' }}>
              {renderMapView(
                `${isFullScreen ? styles.fullMap : styles.map}`,
                <CenterTopBox className={styles.centerBox} />
              )}
              {renderXrayButton()}
              {renderEnergyFlowButton()}
            </div>
          </div>
        ) : (
          <>{renderCenterStandardScreen}</>
        )
      ) : (
        <div
          className={classNames(styles.inner_center, !loadMap ? styles.inner_center_no_map : '')}
          style={{ background: !loadMap ? '#111D2C' : 'transparent' }}
        >
          {loadMap && renderMapView(styles.map)}
          {renderXrayButton()}
          {renderEnergyFlowButton()}
          {loadMap && (
            <div className={styles.center_center_box}>
              <CenterTopBox className={styles.centerBox} />
            </div>
          )}
          <div
            className={classNames(styles.content_layer, !loadMap ? styles.content_layer_no_map : '')}
            style={{ justifyContent: !loadMap ? 'center' : undefined, gap: !loadMap ? 8 : undefined }}
          >
            <div
              className={classNames(
                styles.center_left_box,
                !isStandardSize && loadMap ? styles.center_left_box_collapsed : '',
                !loadMap ? styles.noMapWidth : '',
                !loadMap ? styles.noMapColumn : ''
              )}
            >
              <LeftBox />
            </div>
            <div
              className={classNames(
                styles.center_right_box,
                !loadMap ? styles.noMapWidth : '',
                !isStandardSize && loadMap ? styles.center_right_box_collapsed : '',
                !loadMap ? styles.noMapColumn : ''
              )}
            >
              <RightBox />
            </div>
          </div>
          {/* AI助手锚点（用于计算初始位置，不可见但占位） */}
          {!isStandardSize && loadMap && (
            <div
              ref={aiAnchorRef}
              className={styles.aiRobotWrapper}
              style={{ visibility: 'hidden', pointerEvents: 'none' }}
              aria-hidden
            />
          )}
          {/* AI助手（可拖拽） */}
          <AiAssistant
            anchorRef={aiAnchorRef}
            placement="anchor"
            baseSize={AI_ASSISTANT_SIZE}
            pageScale={pageScale}
            enabled={!isStandardSize && loadMap}
          />
        </div>
      )}
      {isStandardSize && !loadMap && (
        <FloatingMoreActions
          actions={[
            {
              key: 'waterEnergyFlow',
              label: '用水能流概览',
              onClick: () => setFlowModalOpen(true),
            },
          ]}
        />
      )}
      <Modal
        open={flowModalOpen}
        footer={null}
        width={isStandardSize ? 800 : 2000}
        destroyOnClose
        centered
        title={
          <Space size={16}>
            <div>用水能流概览</div>
            <Button style={{ padding: 0 }} type="link" onClick={handleOpenEnergyFlowDetail}>
              更多
              <DoubleRightOutlined />
            </Button>
          </Space>
        }
        className={styles.energyFlowModal}
        onCancel={() => setFlowModalOpen(false)}
        getContainer={() => document.getElementById('full_screen_box') ?? document.body}
      >
        <UseWaterEnergyFlow modalMode onRouteParamsChange={handleFlowRouteParamsChange} />
      </Modal>
      <FlowmeterModal
        open={!!selectedFlowmeter}
        flowmeter={selectedFlowmeter}
        onClose={() => setSelectedFlowmeter(undefined)}
      />
    </ShareConfigDataWaterContext.Provider>
  );
};

export default UnityWaterMap;
